import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./MessagingApp.css"; // Replace with your actual CSS file
import { fixArmoredMessage, fixArmoredPublicKey } from "../utils/pgpHelpers";

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

export default function MessagingApp({
  prefilledMessage = "",
  setPrefilledMessage,
  onDecryptMessage,
}) {
  // Component state variables
  const [view, setView] = useState("login"); // views: login, register, send, receive
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // New state variable for the public key during registration
  const [regPublicKey, setRegPublicKey] = useState("");

  // Changed from receiverId to receiverUsername
  const [receiverUsername, setReceiverUsername] = useState("");
  const [messageContent, setMessageContent] = useState(prefilledMessage);
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState("");

  // Base URL for your API (adjust via environment variable if needed)
  const apiUrl =
    process.env.REACT_APP_API_URL || "https://api.online-encryption.ovh";

  // Helper: convert an object to URL-encoded form data
  const toFormData = (data) => new URLSearchParams(data).toString();

  // Auto-login: Check if the user is already connected
  useEffect(() => {
    fetch(`${apiUrl}/is_connected`, {
      method: "GET",
      credentials: "include", // ensure cookies are sent
    })
      .then((res) => {
        if (res.ok) {
          setFeedback("Auto-login successful!");
          setView("send");
        }
      })
      .catch((err) => {
        console.error("Auto-login check failed:", err);
      });
  }, [apiUrl]);

  // Prefill message when provided by the encryptor
  useEffect(() => {
    if (prefilledMessage) {
      setMessageContent(prefilledMessage);
      setView("send");
      if (setPrefilledMessage) {
        setPrefilledMessage("");
      }
    }
  }, [prefilledMessage, setPrefilledMessage]);

  // Handler for user registration with public key included
  const handleRegister = async (e) => {
    e.preventDefault();
    setFeedback("");
    const sanitizedKey = fixArmoredPublicKey(regPublicKey);
    const formData = toFormData({
      username,
      password,
      publicKey: sanitizedKey, // include public key in the registration data
    });

    try {
      const res = await fetch(`${apiUrl}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: formData,
      });
      if (res.ok) {
        setFeedback("Registration successful! Please login.");
        setView("login");
      } else {
        const text = await res.text();
        setFeedback(text || "Registration failed.");
      }
    } catch (err) {
      setFeedback("Error: " + err.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFeedback("");
    const formData = toFormData({ username, password });

    try {
      const res = await fetch(`${apiUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: formData,
      });
      if (res.ok) {
        setFeedback("Login successful!");
        setView("send");
      } else {
        const text = await res.text();
        setFeedback(text || "Login failed.");
      }
    } catch (err) {
      setFeedback("Error: " + err.message);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setFeedback("");

    // Normalize the PGP message format before sending
    const sanitized = fixArmoredMessage(messageContent);
    // Note: send the receiver's username, not an ID
    const formData = toFormData({
      receiver_username: receiverUsername,
      message: sanitized,
    });

    try {
      const res = await fetch(`${apiUrl}/send_message`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: formData,
      });
      if (res.ok) {
        setFeedback("Message sent successfully!");
        setMessageContent("");
      } else {
        const text = await res.text();
        setFeedback(text || "Failed to send message.");
      }
    } catch (err) {
      setFeedback("Error: " + err.message);
    }
  };

  const handleReceiveMessages = async () => {
    setFeedback("");
    try {
      const res = await fetch(`${apiUrl}/receive_message`, {
        method: "GET",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      } else {
        const text = await res.text();
        setFeedback(text || "Failed to retrieve messages.");
      }
    } catch (err) {
      setFeedback("Error: " + err.message);
    }
  };

  return (
    <div className="app-container">
      <AnimatePresence mode="wait">
        {view === "login" && (
          <motion.div
            key="login"
            className="card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <h1>Login</h1>
            {feedback && <p className="feedback">{feedback}</p>}
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn">
                Login
              </button>
            </form>
            <p>
              Don't have an account?{" "}
              <button
                className="link-button"
                onClick={() => {
                  setView("register");
                  setFeedback("");
                }}
              >
                Register here
              </button>
            </p>
          </motion.div>
        )}

        {view === "register" && (
          <motion.div
            key="register"
            className="card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <h1>Register</h1>
            {feedback && <p className="feedback">{feedback}</p>}
            <form onSubmit={handleRegister}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Public Key</label>
                <textarea
                  value={regPublicKey}
                  onChange={(e) => setRegPublicKey(e.target.value)}
                  placeholder="Paste your PGP public key here..."
                  required
                />
              </div>
              <button type="submit" className="btn">
                Register
              </button>
            </form>
            <p>
              Already have an account?{" "}
              <button
                className="link-button"
                onClick={() => {
                  setView("login");
                  setFeedback("");
                }}
              >
                Login here
              </button>
            </p>
          </motion.div>
        )}

        {view === "send" && (
          <motion.div
            key="send"
            className="card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <h1>Send Message</h1>
            {feedback && <p className="feedback">{feedback}</p>}
            <form onSubmit={handleSendMessage}>
              <div className="form-group">
                <label>Receiver Username</label>
                <input
                  type="text"
                  value={receiverUsername}
                  onChange={(e) => setReceiverUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Message (PGP Encrypted)</label>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Enter your PGP encrypted message..."
                  rows="5"
                  required
                />
              </div>
              <button type="submit" className="btn">
                Send
              </button>
            </form>
            <button
              className="btn secondary"
              onClick={() => {
                setView("receive");
                handleReceiveMessages();
              }}
            >
              View Received Messages
            </button>
          </motion.div>
        )}

        {view === "receive" && (
          <motion.div
            key="receive"
            className="card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <h1>Received Messages</h1>
            {feedback && <p className="feedback">{feedback}</p>}
            <button className="btn" onClick={handleReceiveMessages}>
              Refresh
            </button>
            <ul className="messages-list">
              {messages.length > 0 ? (
                messages.map((msg, idx) => (
                  <li key={idx}>
                    {msg.message}
                    {onDecryptMessage && (
                      <button
                        className="btn secondary"
                        onClick={() => onDecryptMessage(msg.message)}
                        style={{ marginTop: "0.5rem" }}
                      >
                        Decrypt
                      </button>
                    )}
                  </li>
                ))
              ) : (
                <li>No messages available.</li>
              )}
            </ul>
            <button className="btn secondary" onClick={() => setView("send")}>
              Back to Send Message
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
